import React from 'react';
import clsx from 'clsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgAngleLeft from '@icons/angle-left.svg?react';
import SvgAngleRight from '@icons/angle-right.svg?react';

interface ComponentProps {
  side: 'left' | 'right';
  tooltip: string;
  onClick: () => void;
  dataTestId?: string;
  /** What it lies on, so it blends in: the tab strip (default) or a table header. */
  surface?: 'strip' | 'header';
}

const SURFACE_CLASS = {
  strip: { button: 'bg-base-tertiary', fade: 'from-base-tertiary' },
  header: { button: 'bg-content-quaternary', fade: 'from-content-quaternary' },
};

/**
 * The scroll arrow at one end of a sideways strip (tabs). It lies over the strip's edge instead of
 * taking a place next to it, so the tabs never move when it appears or goes. Since it disappears
 * once the strip is scrolled to that end, the last tab is fully visible exactly then. A short fade
 * towards the tabs shows that there is more under it.
 *
 * Place it inside a positioned parent (`relative`, `sticky`) that wraps the scrolling element, after
 * that element, and give the scrolling element `relative z-0`: its own layer below the arrow. Without
 * that, a positioned tab inside the scroller can be drawn over the arrow (seen in the expedition
 * detail). Show it only while there is overflow on its side (see `useScrollOverflow`).
 */
const ScrollEdgeArrow: React.FC<ComponentProps> = ({ side, tooltip, onClick, dataTestId, surface = 'strip' }) => (
  // translateZ(0): its own compositing layer. Over a scroll container the browser otherwise stopped
  // drawing it after an unrelated repaint (opening a dropdown above the tabs) until the strip scrolled.
  <div className={clsx('absolute inset-y-0 z-[1] flex [transform:translateZ(0)]', side === 'left' ? 'left-0 flex-row' : 'right-0 flex-row-reverse')}>
    <RingilButton
      mode="text-only"
      icon={side === 'left' ? <SvgAngleLeft className="w-3" /> : <SvgAngleRight className="w-3" />}
      tooltip={tooltip}
      onClick={onClick}
      className={clsx('h-full w-7 px-0 rounded-none', SURFACE_CLASS[surface].button)}
      dataTestId={dataTestId}
    />
    <span
      className={clsx('pointer-events-none w-3 to-transparent', SURFACE_CLASS[surface].fade, side === 'left' ? 'bg-gradient-to-r' : 'bg-gradient-to-l')}
      aria-hidden
    />
  </div>
);

export default ScrollEdgeArrow;
